import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, Pagination,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PURPLE = "#244C77";
const SECONDARY = "#4A7BA8";

const STATUS_CONFIG = {
  pending:  { label: "Pending",  color: "#d97706", bg: "#fffbeb" },
  approved: { label: "Approved", color: "#16a34a", bg: "#f0fdf4" },
  rejected: { label: "Rejected", color: "#dc2626", bg: "#fef2f2" },
};

const LEAVE_TYPE_CONFIG = {
  Regular:   { color: "#2196f3", bg: "#e3f2fd" },
  Mandatory: { color: "#336599", bg: "#f5f3ff" },
  Medical:   { color: "#dc2626", bg: "#fef2f2" },
  Personal:  { color: "#16a34a", bg: "#f0fdf4" },
  Emergency: { color: "#f59e0b", bg: "#fffbeb" },
};

const FALLBACK_LEAVES = [
  {
    id: "1", staffName: "Mrs. Kavitha Rajan", staffType: "Teacher",
    grade: "Nilai 4", section: "A", leaveFrom: "2025-02-10", leaveTo: "2025-02-12",
    totalDays: 3, leaveType: "Medical", reason: "Medical appointment requiring 3 days rest",
    submittedDate: "2025-02-05", status: "pending"
  },
  {
    id: "2", staffName: "Mr. Suresh Kumar", staffType: "Teacher",
    grade: "Nilai 6", section: "B", leaveFrom: "2025-02-15", leaveTo: "2025-02-15",
    totalDays: 1, leaveType: "Personal", reason: "Family function",
    submittedDate: "2025-02-10", status: "pending"
  },
  {
    id: "3", staffName: "Mrs. Anitha Devi", staffType: "Para-Professional",
    grade: "Mazhalai", section: "A", leaveFrom: "2025-03-01", leaveTo: "2025-03-03",
    totalDays: 3, leaveType: "Mandatory", reason: "State government mandated leave",
    submittedDate: "2025-02-20", status: "approved"
  },
  {
    id: "4", staffName: "Mr. Venkat Prasad", staffType: "Teacher",
    grade: "Nilai 7", section: "A", leaveFrom: "2025-03-10", leaveTo: "2025-03-10",
    totalDays: 1, leaveType: "Regular", reason: "Personal emergency",
    submittedDate: "2025-03-08", status: "pending"
  },
  {
    id: "5", staffName: "Mrs. Deepa Sundaram", staffType: "Teacher",
    grade: "Nilai 2", section: "C", leaveFrom: "2025-03-20", leaveTo: "2025-03-22",
    totalDays: 3, leaveType: "Regular", reason: "Attending teacher training workshop",
    submittedDate: "2025-03-15", status: "rejected"
  },
];

const GRADES   = ["All", "MunMazhalai", "Mazhalai", "Nilai 1", "Nilai 2", "Nilai 3",
  "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];
const SECTIONS = ["All", "A", "B", "C", "D"];
const STAFF_TYPES = ["All", "Teacher", "Para-Professional", "Admin"];
const ROWS_PER_PAGE = 10;

const PrincipalLeaveApprovals = () => {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [gradeFilter, setGradeFilter]     = useState("All");
  const [sectionFilter, setSectionFilter] = useState("All");
  const [staffTypeFilter, setStaffTypeFilter] = useState("All");
  const [page, setPage]             = useState(1);
  const [actionDialog, setActionDialog] = useState({ open: false, row: null, action: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]           = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("principal/leave?status=pending");
      const data = res?.data?.leaves || res?.data || res || [];
      setRows(Array.isArray(data) && data.length > 0 ? data : FALLBACK_LEAVES);
    } catch (_) {
      setRows(FALLBACK_LEAVES);
      setToast({ open: true, severity: "warning", message: "Showing demo data — API unavailable" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    const s = filterText.toLowerCase();
    const matchText = !s ||
      (r.staffName || "").toLowerCase().includes(s) ||
      (r.grade || "").toLowerCase().includes(s) ||
      (r.leaveType || "").toLowerCase().includes(s) ||
      (r.reason || "").toLowerCase().includes(s);
    const matchGrade    = gradeFilter     === "All" || r.grade     === gradeFilter;
    const matchSection  = sectionFilter   === "All" || r.section   === sectionFilter;
    const matchStaff    = staffTypeFilter === "All" || r.staffType === staffTypeFilter;
    return matchText && matchGrade && matchSection && matchStaff;
  });

  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows  = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const handleAction = async () => {
    const { row, action, note } = actionDialog;
    if (!row) return;
    setSubmitting(true);
    try {
      await putData(`principal/leave/${row.id}/${action}`, { note });
      setToast({ open: true, severity: "success", message: `Leave ${action}d successfully!` });
      setActionDialog({ open: false, row: null, action: "", note: "" });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Action failed." });
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = rows.filter(r => r.status === "pending").length;

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Chip label={cfg.label} size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", borderRadius: "20px" }} />
    );
  };

  const LeaveTypeChip = ({ type }) => {
    const cfg = LEAVE_TYPE_CONFIG[type] || { color: "#6B7280", bg: "#f3f4f6" };
    return (
      <Chip label={type || "—"} size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontSize: "11px" }} />
    );
  };

  return (
    <Box sx={{ p: 3, fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Leave Approvals</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BeachAccessIcon sx={{ color: PURPLE, fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#111827" }}>Leave Approvals</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Review and approve staff leave requests
            </Typography>
          </Box>
          {pendingCount > 0 && (
            <Chip
              label={`${pendingCount} pending`}
              size="small"
              sx={{ backgroundColor: "#fffbeb", color: "#d97706", fontWeight: 600, fontSize: "12px" }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<FilterListIcon />} size="small" onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Filters
          </Button>
          <Button
            startIcon={<FileDownloadOutlinedIcon />} size="small"
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Filter Panel */}
      {showFilter && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #E5E7EB" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                size="small" fullWidth
                placeholder="Search by staff name, grade, leave type..."
                value={filterText} onChange={e => { setFilterText(e.target.value); setPage(1); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Grade</InputLabel>
                <Select value={gradeFilter} onChange={e => { setGradeFilter(e.target.value); setPage(1); }} label="Grade" sx={{ borderRadius: "8px" }}>
                  {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Section</InputLabel>
                <Select value={sectionFilter} onChange={e => { setSectionFilter(e.target.value); setPage(1); }} label="Section" sx={{ borderRadius: "8px" }}>
                  {SECTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Staff Type</InputLabel>
                <Select value={staffTypeFilter} onChange={e => { setStaffTypeFilter(e.target.value); setPage(1); }} label="Staff Type" sx={{ borderRadius: "8px" }}>
                  {STAFF_TYPES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)` }}>
                  <TableRow>
                    {["#", "Staff / Teacher Name", "Grade", "Section", "Staff Type", "Leave Date Range", "Days", "Type", "Reason", "Submitted", "Status", "Actions"].map(h => (
                      <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: "center", py: 6, color: "#8F95B2", fontSize: "14px" }}>
                        No leave requests found
                      </TableCell>
                    </TableRow>
                  ) : pageRows.map((r, idx) => (
                    <TableRow key={r.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                      <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{r.staffName || r.teacherName}</TableCell>
                      <TableCell>
                        <Chip label={r.grade || "—"} size="small" sx={{ backgroundColor: "#EAF1F8", color: PURPLE, fontSize: "11px" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{r.section || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.staffType || "Teacher"} size="small"
                          sx={{ backgroundColor: "#e3f2fd", color: "#2196f3", fontSize: "11px" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#374151", whiteSpace: "nowrap" }}>
                        {r.leaveFrom ? new Date(r.leaveFrom).toLocaleDateString("en-GB") : "—"}
                        {" — "}
                        {r.leaveTo ? new Date(r.leaveTo).toLocaleDateString("en-GB") : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", color: "#374151", textAlign: "center" }}>
                        {r.totalDays ?? "—"}
                      </TableCell>
                      <TableCell><LeaveTypeChip type={r.leaveType} /></TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.reason}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#6B7280", whiteSpace: "nowrap" }}>
                        {r.submittedDate ? new Date(r.submittedDate).toLocaleDateString("en-GB") : "—"}
                      </TableCell>
                      <TableCell><StatusChip status={r.status} /></TableCell>
                      <TableCell>
                        {r.status === "pending" && (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => setActionDialog({ open: true, row: r, action: "approve", note: "" })}
                                sx={{ color: "#16a34a", "&:hover": { backgroundColor: "#f0fdf4" } }}
                              >
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() => setActionDialog({ open: true, row: r, action: "reject", note: "" })}
                                sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}
                              >
                                <CancelOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
                  sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: PURPLE, color: "#fff" } }} />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, row: null, action: "", note: "" })}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>
          {actionDialog.action === "approve" ? "Approve Leave Request" : "Reject Leave Request"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#374151", mb: 0.5 }}>
              Staff: <strong>{actionDialog.row?.staffName || actionDialog.row?.teacherName}</strong>
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280", mb: 1.5 }}>
              {actionDialog.row?.leaveFrom ? new Date(actionDialog.row.leaveFrom).toLocaleDateString("en-GB") : "—"}
              {" to "}
              {actionDialog.row?.leaveTo   ? new Date(actionDialog.row.leaveTo).toLocaleDateString("en-GB")   : "—"}
              {" — "}{actionDialog.row?.totalDays} day(s)
            </Typography>
            <TextField
              fullWidth size="small" multiline rows={3}
              label={actionDialog.action === "approve" ? "Note (optional)" : "Reason for rejection"}
              placeholder={actionDialog.action === "approve" ? "Optional note for approval..." : "Please explain why this leave is rejected..."}
              value={actionDialog.note}
              onChange={e => setActionDialog({ ...actionDialog, note: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setActionDialog({ open: false, row: null, action: "", note: "" })}
            fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            fullWidth variant="contained"
            disabled={submitting}
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, boxShadow: "none",
              backgroundColor: actionDialog.action === "approve" ? "#16a34a" : "#dc2626",
              "&:hover": { backgroundColor: actionDialog.action === "approve" ? "#15803d" : "#b91c1c" }
            }}
          >
            {submitting
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : actionDialog.action === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrincipalLeaveApprovals;
